import { ChatBubbleLeftRightIcon, DocumentTextIcon, EllipsisHorizontalIcon, InboxStackIcon, PaperClipIcon, PlusIcon, RectangleStackIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Discussion } from "@/components/collab/Discussion";
import { EntityFiles } from "@/components/files/Files";
import { Breadcrumbs, EditableTitle, EntityBody, Tabs } from "@/components/page";
import { AddPropertyButton, CustomPropertyRows } from "@/components/properties/Properties";
import { TaskViews } from "@/components/tasks/TaskViews";
import { Button, EmptyState, ErrorState, Field, PageSpinner, ProgressBar, Tag } from "@/components/ui";
import { useFeedback } from "@/components/ui/feedback";
import { Modal } from "@/components/ui/Modal";
import { MenuItem, Popover } from "@/components/ui/Popover";
import { api, errorMessage, isStatus } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys, useAction, useEpic, useSprints } from "@/lib/queries";
import { formatDay, pluralize } from "@/lib/utils";

export function SprintFormDialog({ epicId, open, onClose }: { epicId: string; open: boolean; onClose: () => void }) {
  const { pid, bundle } = useProject();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", goal: "", statusId: bundle.sprintStatuses[0]?.id ?? "", startDate: "", endDate: "" });
  const [error, setError] = useState("");
  const create = useAction(
    () =>
      api.createSprint(pid, {
        epicId,
        title: form.title.trim(),
        goal: form.goal.trim(),
        statusId: form.statusId || null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
      }),
    {
      invalidate: [keys.project(pid)],
      onSuccess: (s) => {
        onClose();
        navigate(`/p/${pid}/sprints/${s.id}`);
      },
      onError: (err) => setError(errorMessage(err)),
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New sprint"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => create.mutate()} loading={create.isPending} disabled={!form.title.trim()}>
            Create sprint
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (form.title.trim()) create.mutate();
        }}
      >
        <Field label="Title">
          <input autoFocus value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="field" placeholder="Sprint 1 (official website + project structure)" dir="auto" />
        </Field>
        <Field label="Sprint goal">
          <textarea value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} rows={2} className="field resize-none" dir="auto" />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Status">
            <select value={form.statusId} onChange={(e) => setForm({ ...form, statusId: e.target.value })} className="field">
              <option value="">No status</option>
              {bundle.sprintStatuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Start">
            <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className="field" />
          </Field>
          <Field label="End">
            <input type="date" value={form.endDate} min={form.startDate || undefined} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className="field" />
          </Field>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}

function SprintList({ epicId }: { epicId: string }) {
  const { pid, canEdit, sprintStatusById } = useProject();
  const sprints = useSprints(pid, epicId);
  const [creating, setCreating] = useState(false);
  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="flex justify-end">
          <Button size="sm" icon={PlusIcon} onClick={() => setCreating(true)}>
            New sprint
          </Button>
        </div>
      )}
      {sprints.isPending && <PageSpinner />}
      {sprints.data?.length === 0 && (
        <EmptyState icon={RectangleStackIcon} title="No sprints yet" action={canEdit && <Button variant="primary" icon={PlusIcon} onClick={() => setCreating(true)}>Plan a sprint</Button>}>
          Break this epic into time-boxed sprints, then add tasks to each sprint.
        </EmptyState>
      )}
      {sprints.data?.map((s) => {
        const status = s.statusId ? sprintStatusById.get(s.statusId) : undefined;
        return (
          <Link key={s.id} to={`/p/${pid}/sprints/${s.id}`} className="block rounded-lg border border-line px-4 py-3 transition-colors hover:bg-hover">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <RectangleStackIcon className="size-4 shrink-0 text-muted" />
              <span className="min-w-0 flex-1 truncate font-medium" dir="auto">
                {s.title}
              </span>
              {status && <Tag color={status.color}>{status.name}</Tag>}
              {(s.startDate || s.endDate) && (
                <span className="text-xs text-muted">
                  {formatDay(s.startDate, true) || "?"} → {formatDay(s.endDate, true) || "?"}
                </span>
              )}
            </div>
            {s.goal && (
              <p className="mt-1 line-clamp-2 ps-7 text-sm text-muted" dir="auto">
                {s.goal}
              </p>
            )}
            <div className="mt-2 flex items-center gap-3 ps-7">
              <ProgressBar done={s.doneCount} total={s.taskCount} className="flex-1" />
              <span className="text-xs text-faint">{pluralize(s.taskCount, "task")}</span>
            </div>
          </Link>
        );
      })}
      {creating && <SprintFormDialog epicId={epicId} open onClose={() => setCreating(false)} />}
    </div>
  );
}

type EpicTab = "description" | "sprints" | "backlog" | "files" | "discussion";

export function EpicPage() {
  const { eid = "" } = useParams();
  const { pid, bundle, canEdit } = useProject();
  const epic = useEpic(pid, eid);
  const sprints = useSprints(pid, eid);
  const navigate = useNavigate();
  const { confirm } = useFeedback();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as EpicTab) || "description";
  const setTab = (t: EpicTab) => {
    const next = new URLSearchParams(params);
    next.set("tab", t);
    setParams(next, { replace: true });
  };
  const rename = useAction((title: string) => api.updateEpic(pid, eid, { title }), { invalidate: [keys.project(pid)] });
  const remove = useAction(() => api.deleteEpic(pid, eid), {
    invalidate: [keys.epics(pid)],
    onSuccess: () => navigate(`/p/${pid}`),
  });

  if (epic.isPending) return <PageSpinner />;
  if (epic.error) return <ErrorState message={isStatus(epic.error, 404) ? "This epic was deleted or doesn't exist." : errorMessage(epic.error)} />;
  const e = epic.data;

  return (
    <>
      <Breadcrumbs
        items={[{ label: bundle.project.name, to: `/p/${pid}` }, { label: e.title }]}
        right={
          canEdit && (
            <Popover
              placement="bottom-end"
              className="w-48 p-1"
              trigger={(t) => (
                <button ref={t.ref} onClick={t.onClick} className="rounded p-1.5 text-muted hover:bg-hover" aria-label="More">
                  <EllipsisHorizontalIcon className="size-5" />
                </button>
              )}
            >
              {(close) => (
                <MenuItem
                  icon={TrashIcon}
                  danger
                  onClick={async () => {
                    close();
                    const ok = await confirm({
                      title: `Delete "${e.title}"?`,
                      message: "All sprints and tasks in this epic will be permanently deleted, including their comments and time logs.",
                      confirmLabel: "Delete epic",
                      danger: true,
                    });
                    if (ok) remove.mutate();
                  }}
                >
                  Delete epic
                </MenuItem>
              )}
            </Popover>
          )
        }
      />
      <div className="px-6 pt-8 md:px-12">
        <div className="max-w-4xl">
          <EditableTitle value={e.title} editable={canEdit} onSave={(v) => rename.mutate(v)} className="mb-4" />
          <div className="space-y-px">
            <CustomPropertyRows entityType="epic" entityId={e.id} values={e.values} />
            <AddPropertyButton entityType="epic" />
          </div>
        </div>
        <div className="mt-6 max-w-4xl">
          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[
              { id: "description", label: "Description", icon: DocumentTextIcon },
              { id: "sprints", label: `Sprints${sprints.data ? ` (${sprints.data.length})` : ""}`, icon: RectangleStackIcon },
              { id: "backlog", label: "Backlog", icon: InboxStackIcon },
              { id: "files", label: "Files", icon: PaperClipIcon },
              { id: "discussion", label: "Discussion", icon: ChatBubbleLeftRightIcon },
            ]}
          />
        </div>
      </div>
      <div className="pt-6 pb-24">
        {tab === "description" && (
          <div className="max-w-4xl px-6 md:px-12">
            <EntityBody entityId={e.id} title={e.title} initial={e.body} save={(body) => api.updateEpic(pid, e.id, { body })} />
          </div>
        )}
        {tab === "sprints" && (
          <div className="max-w-4xl px-6 md:px-12">
            <SprintList epicId={e.id} />
          </div>
        )}
        {tab === "backlog" && (
          <>
            <p className="mb-3 px-6 text-sm text-muted md:px-12">Tasks in this epic that aren't planned into a sprint yet. Open a task to move it into a sprint.</p>
            <TaskViews scope={{ epicId: e.id, backlog: true }} storageKey={`view:${pid}:backlog`} createIn={{ epicId: e.id, sprintId: null }} />
          </>
        )}
        {tab === "files" && (
          <div className="max-w-4xl px-6 md:px-12">
            <EntityFiles entityType="epic" entityId={e.id} title={e.title} />
          </div>
        )}
        {tab === "discussion" && (
          <div className="max-w-4xl px-6 md:px-12">
            <Discussion entityId={e.id} />
          </div>
        )}
      </div>
    </>
  );
}
